import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { AlertCircle, CheckCircle, Loader } from 'lucide-react';

export default function SupabaseStatus() {
  const [status, setStatus] = useState<'checking' | 'connected' | 'error'>('checking');
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    const checkConnection = async () => {
      try {
        const { error, status: responseStatus } = await supabase
          .from('practitioners')
          .select('count', { count: 'exact', head: true });

        if (error) {
          if (responseStatus === 401) {
            throw new Error('Authentication failed. Please check your Supabase configuration.');
          } else if (responseStatus === 403) {
            throw new Error('Access forbidden. Please check your RLS policies.');
          } else {
            throw error;
          }
        }

        setStatus('connected');
        setError(null);
        setRetryCount(0);
      } catch (err: Error | unknown) {
        console.error('Supabase connection error:', err);
        setStatus('error');
        setError(err instanceof Error ? err.message : 'Failed to connect to Supabase');
        
        // Retry logic
        if (retryCount < 3) {
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
          }, Math.min(1000 * Math.pow(2, retryCount), 5000));
        }
      }
    };

    checkConnection();
  }, [retryCount]);

  const statusStyles = {
    checking: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200',
    connected: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200',
    error: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200'
  };

  const StatusIcon = {
    checking: Loader,
    connected: CheckCircle,
    error: AlertCircle
  }[status];

  // Don't render anything if connected
  if (status === 'connected') {
    return null;
  }

  return (
    <div className={`fixed bottom-4 right-4 p-4 rounded-lg shadow-lg ${statusStyles[status]}`}>
      <div className="flex items-center gap-2">
        <StatusIcon className={`h-5 w-5 ${status === 'checking' ? 'animate-spin' : ''}`} />
        <div>
          {status === 'checking' && 'Checking Supabase connection...'}
          {status === 'error' && (
            <>
              <h3 className="font-bold">Connection Error</h3>
              <p className="text-sm">{error}</p>
              {retryCount < 3 && (
                <p className="text-sm mt-1">
                  Retrying in {Math.pow(2, retryCount)} seconds...
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}